import type { QueryClient } from '@tanstack/solid-query';
import { asError } from '@trimble/logging';
import type { ILogger } from '@trimble/logging';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import type { ReferenceType } from '@data-access/geospatial/fx-workspace-service';

import type {
  FxWorkspaceRepository,
  SyncFxWorkspaceReferencesInput,
} from './fx-workspace-repository';
import type { FxWorkspaceReferenceStore } from './create-fx-workspace-reference-store';
import { fxWorkspaceReferencesQueryKey } from './fx-workspace-query';
import type {
  RepositoryScopeGuard,
  WorkspaceScopeGuard,
} from './fx-workspace-scope';

export function createFxWorkspaceReferencePersistQueue(): {
  enqueue(key: string, persist: () => Promise<void>): void;
  clear(): void;
} {
  const chains = new Map<string, Promise<void>>();

  const enqueue = (key: string, persist: () => Promise<void>): void => {
    const previous = chains.get(key) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(persist)
      .catch(() => undefined);
    chains.set(key, next);
    void next.finally(() => {
      if (chains.get(key) === next) {
        chains.delete(key);
      }
    });
  };

  const clear = (): void => {
    chains.clear();
  };

  return { enqueue, clear };
}

function requireReferenceType(type: ReferenceType | undefined): ReferenceType {
  if (type === undefined) {
    throw new Error(
      'Reference type is required to add an FX workspace reference'
    );
  }

  return type;
}

export function createFxWorkspaceReferenceToggle(options: {
  store: FxWorkspaceReferenceStore;
  repository(): FxWorkspaceRepository;
  fetchKey(): string | undefined;
  queryClient: QueryClient;
  scope: RepositoryScopeGuard;
  workspaceScope: WorkspaceScopeGuard;
  persistQueue: ReturnType<typeof createFxWorkspaceReferencePersistQueue>;
  logger: ILogger;
}): (workspaceId: string, refTrn: string, type?: ReferenceType) => void {
  const toggleReferenceVisibility = (
    workspaceId: string,
    refTrn: string,
    type?: ReferenceType
  ): void => {
    const previousRefs = options.store.getWorkspaceReferences(workspaceId);
    const isVisible = previousRefs.has(refTrn);
    const addedReference = isVisible
      ? undefined
      : {
          refTrn,
          type: requireReferenceType(type),
        };

    const scopeSnapshot = options.scope.capture();
    const workspaceSnapshot = options.workspaceScope.capture(workspaceId);
    const key = options.fetchKey();
    const isHydrated = options.store.areWorkspaceReferencesLoaded(workspaceId);

    if (isHydrated && key !== undefined) {
      void options.queryClient.cancelQueries({
        queryKey: fxWorkspaceReferencesQueryKey(key, workspaceId),
      });
    }

    const toggleTokenKey = options.store.referenceToggleTokenKey(
      workspaceId,
      refTrn
    );
    const toggleToken = options.store.bumpReferenceToggleToken(toggleTokenKey);
    const nextRefs = new Set(previousRefs);

    if (isVisible) {
      nextRefs.delete(refTrn);
    } else {
      nextRefs.add(refTrn);
    }

    options.store.setWorkspaceReferenceSet(workspaceId, nextRefs);
    if (isHydrated) {
      options.store.cacheWorkspaceReferences(workspaceId, nextRefs);
    } else {
      options.store.recordPendingToggle(workspaceId, refTrn, isVisible);
    }

    const activeRepository = options.repository();
    const isPersistCurrent = (): boolean =>
      options.scope.isCurrent(scopeSnapshot) &&
      options.workspaceScope.isCurrent(workspaceId, workspaceSnapshot) &&
      options.store.isLatestReferenceToggleToken(toggleTokenKey, toggleToken);

    const persistToggle = async (): Promise<void> => {
      if (!isPersistCurrent()) {
        return;
      }

      try {
        const toAdd = addedReference === undefined ? [] : [addedReference];
        const toRemove = isVisible ? [refTrn] : [];

        await activeRepository.syncReferences(workspaceId, { toAdd, toRemove });
      } catch (error: unknown) {
        if (!isPersistCurrent()) {
          return;
        }

        options.logger.error(
          'Failed to update FX workspace reference',
          { workspaceId, refTrn, isVisible },
          asError(error)
        );

        const currentRefs = options.store.getWorkspaceReferences(workspaceId);
        const rolledBack = new Set(currentRefs);
        if (isVisible) {
          rolledBack.add(refTrn);
        } else {
          rolledBack.delete(refTrn);
        }

        options.store.setWorkspaceReferenceSet(workspaceId, rolledBack);
        if (options.store.areWorkspaceReferencesLoaded(workspaceId)) {
          options.store.cacheWorkspaceReferences(workspaceId, rolledBack);
        } else {
          options.store.revertPendingToggle(workspaceId, refTrn, isVisible);
        }
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.references.toggleFailed'),
        });
      }
    };

    options.persistQueue.enqueue(toggleTokenKey, persistToggle);
  };

  return toggleReferenceVisibility;
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 38 > 15 allowed; flagged for later refactor, not fixed here
export async function syncFxWorkspaceReferences(options: {
  store: FxWorkspaceReferenceStore;
  repository(): FxWorkspaceRepository;
  fetchKey(): string | undefined;
  queryClient: QueryClient;
  scope: RepositoryScopeGuard;
  workspaceScope: WorkspaceScopeGuard;
  logger: ILogger;
  workspaceId: string;
  input: SyncFxWorkspaceReferencesInput;
}): Promise<boolean> {
  const { workspaceId, input } = options;

  if (input.toAdd.length === 0 && input.toRemove.length === 0) {
    return true;
  }

  const scopeSnapshot = options.scope.capture();
  const workspaceSnapshot = options.workspaceScope.capture(workspaceId);
  const key = options.fetchKey();
  const isHydrated = options.store.areWorkspaceReferencesLoaded(workspaceId);
  const persistTokenKey = `sync:${workspaceId}`;
  const persistToken = options.store.bumpReferenceToggleToken(persistTokenKey);

  if (isHydrated && key !== undefined) {
    void options.queryClient.cancelQueries({
      queryKey: fxWorkspaceReferencesQueryKey(key, workspaceId),
    });
  }

  const previousRefs = options.store.getWorkspaceReferences(workspaceId);
  const nextRefs = new Set(previousRefs);

  for (const refTrn of input.toRemove) {
    nextRefs.delete(refTrn);
  }
  for (const add of input.toAdd) {
    nextRefs.add(add.refTrn);
  }

  options.store.setWorkspaceReferenceSet(workspaceId, nextRefs);
  if (isHydrated) {
    options.store.cacheWorkspaceReferences(workspaceId, nextRefs);
  } else {
    for (const refTrn of input.toRemove) {
      if (previousRefs.has(refTrn)) {
        options.store.recordPendingToggle(workspaceId, refTrn, true);
      }
    }
    for (const add of input.toAdd) {
      if (!previousRefs.has(add.refTrn)) {
        options.store.recordPendingToggle(workspaceId, add.refTrn, false);
      }
    }
  }

  const isPersistCurrent = (): boolean =>
    options.scope.isCurrent(scopeSnapshot) &&
    options.workspaceScope.isCurrent(workspaceId, workspaceSnapshot) &&
    options.store.isLatestReferenceToggleToken(persistTokenKey, persistToken);

  try {
    await options.repository().syncReferences(workspaceId, {
      toAdd: [...input.toAdd],
      toRemove: [...input.toRemove],
    });
    return isPersistCurrent();
  } catch (error: unknown) {
    if (!isPersistCurrent()) {
      return false;
    }

    options.logger.error(
      'Failed to sync FX workspace references',
      { workspaceId, toAdd: input.toAdd, toRemove: input.toRemove },
      asError(error)
    );

    options.store.setWorkspaceReferenceSet(workspaceId, previousRefs);
    if (options.store.areWorkspaceReferencesLoaded(workspaceId)) {
      options.store.cacheWorkspaceReferences(workspaceId, previousRefs);
    } else {
      for (const refTrn of input.toRemove) {
        if (previousRefs.has(refTrn)) {
          options.store.revertPendingToggle(workspaceId, refTrn, true);
        }
      }
      for (const add of input.toAdd) {
        if (!previousRefs.has(add.refTrn)) {
          options.store.revertPendingToggle(workspaceId, add.refTrn, false);
        }
      }
    }

    NotificationEventBus.emit({
      type: 'error',
      message: t('mobileMapping.fxWorkspace.workspaceDataEditing.saveFailed'),
    });

    return false;
  }
}
